import { memo, useMemo } from 'react';
import styled from 'styled-components';

import { useDesignerUIContext } from '../../hooks/useDesignerUIState';
import { LessonAttachment } from '../../services/backendService/backendService';
import { SlideElement } from './slide.types';
import SlideElementAttachmentPassage from './SlideElementAttachmentPassage';
import SlideElementIframe from './SlideElementIframe';
import SlideElementImage from './SlideElementImage';
import SlideElementShape from './SlideElementShape';
import SlideElementSvg from './SlideElementSvg';
import SlideElementTextViewer from './SlideElementTextViewer';
import {
  isSlideElementAttachmentPassage,
  isSlideElementIframe,
  isSlideElementImage,
  isSlideElementShape,
  isSlideElementSvg,
  isSlideElementText,
} from './slideUtil';

const SlideElementEditor = ({
  element,
  aspect,
  isDiscoverTemplate,
  selectedElements,
  isOwner,
  lessonAttachments,
}: {
  element: SlideElement;
  selectedElements: SlideElement[] | null;
  aspect: { width: number; height: number };
  isDiscoverTemplate: boolean;
  isOwner: boolean;
  lessonAttachments?: LessonAttachment[];
}) => {
  const isSelected = useMemo(() => {
    if (!selectedElements) return false;
    const isSelected = selectedElements.find(e => e.id === element.id);
    return isSelected ? true : false;
  }, [element.id, selectedElements]);
  // Listening to transform properties only if this is the selected element
  const elementTransformationProperties = useDesignerUIContext(
    context => context.state.elementTransformationProperties,
  );

  const thisElementTransformProperties = useMemo(() => {
    if (elementTransformationProperties) {
      return elementTransformationProperties.find(e => e.id === element.id);
    }
    return undefined;
  }, [element.id, elementTransformationProperties]);
  const editingTextElementId = useDesignerUIContext(
    context => context.state.textElementIdBeingEdited,
  );
  const scale = thisElementTransformProperties?.transformScale || element.scale || 1;
  const translateX = thisElementTransformProperties
    ? thisElementTransformProperties.transformX
    : element.x;
  const translateY = thisElementTransformProperties
    ? thisElementTransformProperties.transformY
    : element.y;
  const angle = thisElementTransformProperties
    ? thisElementTransformProperties.transformAngle
    : element.angle;
  const width = thisElementTransformProperties
    ? thisElementTransformProperties.transformWidth
    : element.width;
  const height = thisElementTransformProperties
    ? thisElementTransformProperties.transformHeight
    : element.height;
  return (
    <SlideElementContainer
      id={element.id}
      style={{
        transform: `translate3d(${translateX}px, ${translateY}px, 0 ) rotate(${angle}deg)`,
        cursor: element.lockedPosition ? 'default' : 'pointer',
        transformOrigin: 'center',
        width: width,
        height: height,
        opacity: element.opacity !== undefined ? element.opacity : 1,
      }}
      $selected={isSelected}
      $aspect={aspect}
    >
      <div
        style={{
          width: width / scale,
          height: height / scale,
          transform: `scale(${scale || 1})`,
          transformOrigin: 'top left',
        }}
      >
        {/* RENDER THE DIFFERENT ELEMENT TYPES */}
        {isSlideElementImage(element) && (
          <SlideElementImage
            element={element}
            isAuthenticated={true}
            isOwner={isOwner}
            isDiscoverTemplate={isDiscoverTemplate}
          />
        )}
        {isSlideElementSvg(element) && (
          <SlideElementSvg
            element={element}
            isAuthenticated={true}
            isOwner={isOwner}
            isDiscoverTemplate={isDiscoverTemplate}
          />
        )}
        {isSlideElementShape(element) && (
          <>
            <SlideElementShape element={element} />
          </>
        )}
        {isSlideElementAttachmentPassage(element) && (
          <SlideElementAttachmentPassage
            element={element}
            slideMode={'edit'}
            lessonAttachments={lessonAttachments}
            isFromDiscover={false}
          />
        )}
        {isSlideElementIframe(element) && (
          <SlideElementIframe element={element} slideMode={'edit'} />
        )}
        {/* 
        When rendering out text we default show the TextViwer which have their own slateEditor. But for selectedElement we 
        use the richTextEditor from the useDesigner and render the SlideElementTextEditor
        //TODO rewrite isEditingElement to clearly that is being hidden when being edited
        */}
        {isSlideElementText(element) && editingTextElementId !== element.id && (
          <SlideElementTextViewer
            element={element}
            richTextEditorPrefix={'slide-element-core-viwer'}
            showPlaceHolder={true}
            style={{ cursor: 'pointer' }}
            content={element.textValue}
          />
        )}
      </div>
    </SlideElementContainer>
  );
};

const SlideElementContainer = styled.div<{
  $selected?: boolean;
  $aspect: { height: number; width: number };
  $showHoverOutline?: boolean;
}>`
  touch-action: none;
  position: absolute;
  top: 0;

  left: 0;

  border-radius: 10px;
  cursor: pointer;
  p,
  input {
    width: 100%;
    margin: 0;
    border: none;
    outline: none;
  }
`;

export default memo(SlideElementEditor);
